import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { notification } from "../../../utils/Notifications";
import {
  assignGuidesToDeliveryMan,
  assignGuidesToDeliveryManBySuperadmin,
} from "../../../api/guidesApi";
import useLogout from "../../../hooks/auth/authHook.js";
import {
  Button,
  Container,
  Dialog,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import GuidesQRDialog from "../GuidesQRDialog";
import styles from "./guidesAssingUserDialogStyles.js";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

GuidesAssingUserDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deliverers: PropTypes.array,
  selectedZone: PropTypes.number,
};

export default function GuidesAssingUserDialog({
  onClose,
  open,
  selectedGuides,
  fetchGuides,
  fetchUnassingnedGuides,
  deliverers = [],
  selectedZone,
}) {
  const [logout] = useLogout();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [urlPDF, setUrlPDF] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);
  const { hasAllPermissions } = usePermissions();

  const handleDialogOpen = (dialogName) => {
    setOpenDialog(dialogName);
  };

  const handleDialogClose = () => {
    setOpenDialog(null);
    onClose();
  };

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const handleAssingUserLotAdminClick = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    const selectedUser = deliverers.find(
      (user) => user.id_user === selectedValue
    );

    const assignFunction = isSuperAdmin
      ? assignGuidesToDeliveryManBySuperadmin
      : assignGuidesToDeliveryMan;

    const assignParams = isSuperAdmin
      ? [selectedUser.name_complete, selectedGuides, selectedZone]
      : [selectedUser.name_complete, selectedGuides];

    assignFunction(...assignParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({
            text: response.data.status_Message,
            type: "error",
          });
        } else if (response.data.status_Code === 201) {
          setUrlPDF(response.data.url_pdf_batch_route);
          fetchGuides();
          fetchUnassingnedGuides();
          setIsLoading(false);
          handleDialogOpen("GuideQRDialog");
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          setIsLoading(false);
          onClose();
          logout();
        } else {
          notification({
            text: "Error al asignar las guías",
            type: "error",
          });
          setIsLoading(false);
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
        setIsLoading(false);
        onClose();
      })
      .finally(() => {
        clearFields();
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      clearFields();
    }
  }, [open]);

  const clearFields = () => {
    setSelectedValue("");
  };

  return (
    <Dialog
      onClose={handleDialogClose}
      open={open}
      PaperProps={{ style: styles.dialog }}
    >
      <GuidesQRDialog
        open={openDialog === "GuideQRDialog"}
        onClose={handleDialogClose}
        handleDialogOpen={handleDialogOpen}
        selectedGuides={selectedGuides}
        urlPDF={urlPDF}
      />

      <Container style={styles.mainContainer}>
        <Typography component="div" variant="h5" style={styles.title}>
          Selecciona al repartidor
        </Typography>
        <Typography component="div" variant="body1" style={styles.subtitle}>
          Los lotes serán asignados de manera automática
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="demo-simple-select-label">Repartidor</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedValue}
                label="Repartidor"
                onChange={handleChange}
              >
                {deliverers?.map((deliveryMen) => {
                  return (
                    <MenuItem
                      key={deliveryMen.id_user}
                      value={deliveryMen.id_user}
                    >
                      {deliveryMen.name_complete}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Container style={styles.buttonsContainer}>
          <Button
            variant="standard"
            color="primary"
            onClick={() => {
              onClose();
            }}
            style={styles.cancelButton}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!selectedValue}
            onClick={() => {
              handleAssingUserLotAdminClick();
            }}
            sx={selectedValue ? styles.activeButton : styles.inactiveButton}
          >
            {isLoading ? (
              <CircularProgress size={24} style={styles.circularProgress} />
            ) : (
              "Asignar"
            )}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
